import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Typography,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import api from '../../lib/api';
import { adminStyles } from '../../styles/adminStyles';
import AdminPageShell from '../../components/admin/AdminPageShell';
import { normalizeListResponse } from '../../lib/sectionClientsAggregate';
import { mapOrgClientsToPickerOptions, organizationIdFrom } from '../../lib/orgClientsPicker';
import ComplexClientPicker from '../../components/sections/ComplexClientPicker';

class StandaloneBookings extends Component {
  constructor(props) {
    super(props);

    this.state = {
      bookings: [],
      orderBy: 'date',
      order: 'desc',
      loading: false,
      snackbarOpen: false,
      snackbarMessage: '',
      dialogOpen: false,
      editingBookingId: null,
      availableItems: [],
      selectedItems: [],
      formData: {
        clientName: '',
        clientPhone: '',
        date: moment().format('YYYY-MM-DD'),
        startTime: '',
        endTime: '',
      },
      availabilityInfo: {}, // { itemIndex: { available, availableQuantity, totalQuantity } }
      orgClientOptions: [],
      organizationId: null,
      clientKey: '',
      clientUserId: null,
    };

    this.orderBy = this.orderBy.bind(this);
  }

  componentDidMount() {
    this.loadBookings();
  }

  async loadBookings() {
    this.setState({ loading: true });
    try {
      const bookings = await api.getStandaloneRentalBookings();
      this.setState({ bookings: bookings || [] });
    } catch (error) {
      this.showSnackbar(`Ошибка загрузки: ${error.message}`);
    } finally {
      this.setState({ loading: false });
    }
  }

  async loadStandaloneItems() {
    try {
      const items = await api.getStandaloneRentalItems();
      this.setState({ availableItems: items || [] }, () => this.loadOrgClientsFromItems());
    } catch (error) {
      this.showSnackbar(`Ошибка загрузки услуг: ${error.message}`);
    }
  }

  loadOrgClientsFromItems = async () => {
    const items = this.state.availableItems || [];
    const complexId = items.reduce((found, item) => {
      if (found) return found;
      const cid = item.sportComplexId || item.SportComplexId;
      return cid ? Number(cid) : null;
    }, null);
    if (!complexId) {
      this.setState({ orgClientOptions: [], organizationId: null });
      return;
    }
    try {
      const org = await api.getOrganizationByComplex(complexId);
      const orgId = organizationIdFrom(org);
      if (!orgId) {
        this.setState({ orgClientOptions: [], organizationId: null });
        return;
      }
      let picker = [];
      try {
        picker = normalizeListResponse(await api.getOrganizationClientsPicker(orgId));
      } catch (e) {
        picker = [];
      }
      if (!picker.length) {
        picker = normalizeListResponse(await api.getOrganizationClients(orgId));
      }
      this.setState({
        organizationId: orgId,
        orgClientOptions: mapOrgClientsToPickerOptions(picker),
      });
    } catch (e) {
      this.setState({ orgClientOptions: [], organizationId: null });
    }
  };

  orderBy(field) {
    if (field === this.state.orderBy) {
      this.setState({ order: this.state.order === 'desc' ? 'asc' : 'desc' });
    } else {
      this.setState({ orderBy: field });
    }
  }

  handleOpenDialog = async () => {
    await this.loadStandaloneItems();
    this.setState({ 
      dialogOpen: true,
      editingBookingId: null,
      selectedItems: [],
      formData: {
        clientName: '',
        clientPhone: '',
        date: moment().format('YYYY-MM-DD'),
        startTime: '',
        endTime: '',
      },
      clientKey: '',
      clientUserId: null,
    });
  };

  handleEditBooking = async (booking) => {
    await this.loadStandaloneItems();
    
    // Определяем, есть ли PerHour услуги
    const hasPerHourItems = booking.items?.some(item => {
      const rentalItem = this.state.availableItems.find(i => i.id === item.rentalItemId);
      return rentalItem && (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour');
    });

    let startTime = '';
    let endTime = '';
    
    if (hasPerHourItems && booking.items && booking.items.length > 0) {
      const firstItem = booking.items[0];
      if (firstItem.startDate) {
        // Используем локальное время, не UTC
        startTime = moment(firstItem.startDate).local().format('HH:mm');
      }
      if (firstItem.endDate) {
        // Используем локальное время, не UTC
        endTime = moment(firstItem.endDate).local().format('HH:mm');
      }
    }

    this.setState({
      dialogOpen: true,
      editingBookingId: booking.id,
      selectedItems: booking.items?.map(item => ({
        rentalItemId: item.rentalItemId,
        quantity: item.quantity,
      })) || [],
      formData: {
        clientName: booking.clientName || '',
        clientPhone: booking.clientPhone || '',
        date: moment(booking.date).format('YYYY-MM-DD'),
        startTime,
        endTime,
      },
    });
  };

  handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это бронирование?')) {
      return;
    }

    this.setState({ loading: true });
    try {
      await api.deleteStandaloneRentalBooking(bookingId);
      this.showSnackbar('Бронирование успешно удалено');
      this.loadBookings();
    } catch (error) {
      this.showSnackbar(`Ошибка удаления: ${error.message}`);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleCloseDialog = () => {
    this.setState({ 
      dialogOpen: false,
      editingBookingId: null,
      availabilityInfo: {},
    });
  };

  handleAddItem = () => {
    this.setState(prevState => ({
      selectedItems: [...prevState.selectedItems, {
        rentalItemId: '',
        quantity: 1,
      }],
    }));
  };

  handleRemoveItem = (index) => {
    this.setState(prevState => ({
      selectedItems: prevState.selectedItems.filter((_, i) => i !== index),
    }));
  };

  handleItemChange = async (index, field, value) => {
    this.setState(prevState => {
      const newItems = [...prevState.selectedItems];
      newItems[index] = { ...newItems[index], [field]: value };
      return { selectedItems: newItems };
    }, () => {
      // Проверяем доступность после обновления состояния
      if (field === 'rentalItemId' || field === 'quantity') {
        this.checkAvailability(index);
      }
    });
  };

  checkAvailability = async (itemIndex) => {
    const { selectedItems, formData, editingBookingId } = this.state;
    const item = selectedItems[itemIndex];
    
    if (!item || !item.rentalItemId) {
      this.setState(prevState => ({
        availabilityInfo: { ...prevState.availabilityInfo, [itemIndex]: null }
      }));
      return;
    }

    const rentalItem = this.state.availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
    if (!rentalItem || !rentalItem.quantity) {
      // Если количество не ограничено, не проверяем
      this.setState(prevState => ({
        availabilityInfo: { ...prevState.availabilityInfo, [itemIndex]: { available: true, availableQuantity: null, totalQuantity: null } }
      }));
      return;
    }

    const isPerHour = rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour';
    
    if (isPerHour && (!formData.startTime || !formData.endTime)) {
      // Для PerHour нужно время
      this.setState(prevState => ({
        availabilityInfo: { ...prevState.availabilityInfo, [itemIndex]: null }
      }));
      return;
    }

    try {
      const startMoment = isPerHour 
        ? moment(`${formData.date} ${formData.startTime}`)
        : moment(`${formData.date} 00:00`);
      let endMoment = isPerHour 
        ? moment(`${formData.date} ${formData.endTime}`)
        : null;

      if (isPerHour && formData.endTime < formData.startTime) {
        endMoment.add(1, 'day');
      }

      const availability = await api.checkRentalItemAvailability(
        parseInt(item.rentalItemId, 10),
        startMoment.toDate(),
        endMoment ? endMoment.toDate() : null,
        parseInt(item.quantity, 10) || 1,
        editingBookingId || null
      );

      this.setState(prevState => ({
        availabilityInfo: { ...prevState.availabilityInfo, [itemIndex]: availability }
      }));
    } catch (error) {
      console.error('Ошибка проверки доступности:', error);
    }
  };

  handleSave = async () => {
    const { formData, selectedItems } = this.state;

    if (!formData.clientName || !formData.clientPhone) {
      this.showSnackbar('Необходимо указать имя и телефон клиента');
      return;
    }

    if (selectedItems.length === 0) {
      this.showSnackbar('Необходимо добавить хотя бы одну услугу');
      return;
    }

    // Проверяем, есть ли услуги с почасовой тарификацией
    const hasPerHourItems = selectedItems.some(item => {
      const rentalItem = this.state.availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
      return rentalItem && (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour');
    });

    // Валидация для PerHour услуг
    if (hasPerHourItems) {
      if (!formData.startTime || formData.startTime.trim() === '') {
        this.showSnackbar('Необходимо указать время начала');
        return;
      }
      if (!formData.endTime || formData.endTime.trim() === '') {
        this.showSnackbar('Необходимо указать время окончания');
        return;
      }
      
      const startMoment = moment(`${formData.date} ${formData.startTime}`);
      const endMoment = moment(`${formData.date} ${formData.endTime}`);
      
      // Если время окончания меньше времени начала, значит это следующий день
      if (formData.endTime < formData.startTime) {
        endMoment.add(1, 'day');
      }
      
      if (!startMoment.isValid() || !endMoment.isValid()) {
        this.showSnackbar('Указаны некорректные дата или время');
        return;
      }
      
      if (endMoment.isSameOrBefore(startMoment)) {
        this.showSnackbar('Время окончания должно быть позже времени начала');
        return;
      }
    }

    // Валидация и преобразование данных
    const items = [];
    for (const item of selectedItems) {
      const rentalItem = this.state.availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
      if (!rentalItem) {
        this.showSnackbar(`Услуга с ID ${item.rentalItemId} не найдена`);
        return;
      }
      
      if (!item.rentalItemId || item.rentalItemId === '') {
        this.showSnackbar('Необходимо выбрать услугу для всех позиций');
        return;
      }
      
      if (!item.quantity || item.quantity < 1) {
        this.showSnackbar('Необходимо указать количество для всех услуг');
        return;
      }

      let startDate, endDate = null;

      if (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour') { // PerHour
        // Создаем момент в локальном времени
        const startMoment = moment(`${formData.date} ${formData.startTime}`, 'YYYY-MM-DD HH:mm').local();
        let endMoment = moment(`${formData.date} ${formData.endTime}`, 'YYYY-MM-DD HH:mm').local();
        
        // Если время окончания меньше времени начала, значит это следующий день
        if (formData.endTime < formData.startTime) {
          endMoment.add(1, 'day');
        }
        
        // Отправляем ISO строку, но с явным указанием локального времени
        // Форматируем как ISO, но без Z (чтобы указать, что это локальное время)
        // Бэкенд должен интерпретировать это как локальное время
        startDate = startMoment.format('YYYY-MM-DDTHH:mm:ss');
        endDate = endMoment.format('YYYY-MM-DDTHH:mm:ss');
      } else { // Fixed
        // Для Fixed услуг используем только дату
        startDate = moment(`${formData.date} 00:00`, 'YYYY-MM-DD HH:mm').local().format('YYYY-MM-DDTHH:mm:ss');
      }

      items.push({
        rentalItemId: parseInt(item.rentalItemId, 10),
        quantity: parseInt(item.quantity, 10) || 1,
        startDate: startDate,
        endDate: endDate || null,
      });
    }

    const bookingData = {
      userId: this.state.clientUserId || null,
      clientName: formData.clientName.trim(),
      clientPhone: formData.clientPhone.trim(),
      date: moment(formData.date).startOf('day').toISOString(),
      items,
    };

    this.setState({ loading: true });
    try {
      const { editingBookingId } = this.state;
      
      if (editingBookingId) {
        // Обновление существующего бронирования
        const updateData = {
          date: moment(formData.date).startOf('day').toISOString(),
          startTime: formData.startTime || '',
          endTime: formData.endTime || '',
        };
        await api.updateStandaloneRentalBooking(editingBookingId, updateData);
        this.showSnackbar('Бронирование успешно обновлено');
      } else {
        // Создание нового бронирования
        await api.createStandaloneRentalBooking(bookingData);
        this.showSnackbar('Бронирование успешно создано');
      }
      this.handleCloseDialog();
      this.loadBookings();
    } catch (error) {
      // Показываем понятное сообщение об ошибке
      let errorMessage = 'Произошла ошибка при сохранении бронирования';
      
      if (error.message) {
        // Если сервер вернул конкретное сообщение, используем его
        if (error.message.includes('Недостаточно доступных единиц') || 
            error.message.includes('Недостаточно')) {
          errorMessage = error.message;
        } else if (error.message.includes('Необходимо указать')) {
          errorMessage = error.message;
        } else if (error.message.includes('не найдена') || error.message.includes('не найден')) {
          errorMessage = error.message;
        } else if (error.message.includes('не является')) {
          errorMessage = error.message;
        } else if (error.message.includes('Дата окончания') || error.message.includes('Время окончания')) {
          errorMessage = error.message;
        } else if (error.message.includes('HTTP Error') || error.message.includes('Bad Request')) {
          // Для общих HTTP ошибок показываем более понятное сообщение
          errorMessage = 'Не удалось сохранить бронирование. Проверьте правильность заполнения всех полей.';
        } else {
          errorMessage = error.message;
        }
      }
      
      this.showSnackbar(errorMessage);
    } finally {
      this.setState({ loading: false });
    }
  };

  showSnackbar = (message) => {
    this.setState({ snackbarOpen: true, snackbarMessage: message });
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  render() {
    const { classes } = this.props;
    const { bookings, orderBy, order, loading, snackbarOpen, snackbarMessage, dialogOpen, availableItems, selectedItems, formData } = this.state;

    const sortedBookings = [...bookings].sort((a, b) => {
      let aValue, bValue;
      switch (orderBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'user':
          aValue = a.user?.name || '';
          bValue = b.user?.name || '';
          break;
        case 'price':
          aValue = a.totalPrice || 0;
          bValue = b.totalPrice || 0;
          break;
        case 'state':
          aValue = a.state || '';
          bValue = b.state || '';
          break;
        default:
          return 0;
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });

    const stateLabels = {
      0: 'Архив',
      1: 'Отменено',
      2: 'Открыто',
      3: 'Начали оплачивать',
      4: 'Оплачено',
    };

    return (
      <AdminPageShell
        title="Доп. услуги и прокат"
        actions={
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={this.handleOpenDialog}
          >
            Создать бронирование
          </Button>
        }
      >
        <Paper className={classes.paper} elevation={2}>
          {loading && bookings.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <CircularProgress />
            </div>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'date'}
                      direction={order}
                      onClick={() => this.orderBy('date')}
                    >
                      Дата
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'user'}
                      direction={order}
                      onClick={() => this.orderBy('user')}
                    >
                      Клиент
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Услуги</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'price'}
                      direction={order}
                      onClick={() => this.orderBy('price')}
                    >
                      Цена
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'state'}
                      direction={order}
                      onClick={() => this.orderBy('state')}
                    >
                      Статус
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>Нет бронирований</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedBookings.map((booking) => {
                    // Для PerHour услуг показываем дату из первой услуги, иначе из booking.date
                    const firstItem = booking.items && booking.items.length > 0 ? booking.items[0] : null;
                    const hasPerHourItem = firstItem && firstItem.startDate && firstItem.endDate;
                    const displayDate = hasPerHourItem 
                      ? moment(firstItem.startDate).local().format('DD.MM.YYYY HH:mm')
                      : moment(booking.date).format('DD.MM.YYYY');
                    
                    return (
                      <TableRow key={booking.id}>
                        <TableCell>
                          {displayDate}
                        </TableCell>
                        <TableCell>
                          {booking.user?.name || booking.clientName || 'Неизвестно'} {booking.user?.phone || booking.clientPhone || ''}
                        </TableCell>
                        <TableCell>
                          {booking.items?.map((item, idx) => (
                            <div key={idx}>
                              {item.rentalItem?.name || 'Неизвестная услуга'} 
                              (x{item.quantity})
                              {item.startDate && (
                                <span style={{ fontSize: '0.85em', color: '#666', marginLeft: 8 }}>
                                  {moment(item.startDate).local().format('DD.MM HH:mm')}
                                  {item.endDate && ` - ${moment(item.endDate).local().format('DD.MM HH:mm')}`}
                                </span>
                              )}
                            </div>
                          ))}
                        </TableCell>
                      <TableCell>{booking.totalPrice?.toFixed(2) || '0.00'} ₽</TableCell>
                      <TableCell>{stateLabels[booking.state] || booking.state}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => this.handleEditBooking(booking)}
                          className={classes.outlinedButton}
                          style={{ marginRight: 8 }}
                        >
                          Редактировать
                        </Button>
                        <Button
                          size="small"
                          onClick={() => this.handleDeleteBooking(booking.id)}
                          className={classes.removeButton}
                        >
                          Удалить
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Dialog
          open={dialogOpen}
          onClose={this.handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          <DialogTitle style={{ padding: '24px 24px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
              {this.state.editingBookingId ? 'Редактировать бронирование' : 'Создать бронирование услуги/проката'}
            </span>
          </DialogTitle>
          <DialogContent style={{ padding: 24 }}>
            {this.state.editingBookingId ? (
              <TextField
                label="Имя клиента *"
                value={formData.clientName}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled
              />
            ) : (
              <ComplexClientPicker
                selectId="standalone-rental-client"
                options={this.state.orgClientOptions || []}
                value={this.state.clientKey}
                orgId={this.state.organizationId}
                emptyText="Нет клиентов организации. Добавьте карточку — имя и телефон сохранятся в справочнике."
                onCreated={() => this.loadOrgClientsFromItems()}
                onChange={(key, opt) => {
                  this.setState((prev) => ({
                    clientKey: key || '',
                    clientUserId: opt && opt.userId ? opt.userId : null,
                    formData: {
                      ...prev.formData,
                      clientName: opt ? (opt.name || '') : '',
                      clientPhone: opt ? (opt.phone || '') : '',
                    },
                  }));
                }}
              />
            )}
            {this.state.editingBookingId ? (
            <TextField
              label="Телефон клиента *"
              value={formData.clientPhone}
              fullWidth
              margin="normal"
              variant="outlined"
              disabled
            />
            ) : (this.state.formData.clientName || this.state.formData.clientPhone) ? (
              <Typography variant="caption" color="textSecondary">
                {this.state.formData.clientName}
                {this.state.formData.clientPhone ? ` · ${this.state.formData.clientPhone}` : ''}
              </Typography>
            ) : null}
            <TextField
              label="Дата бронирования *"
              type="date"
              value={formData.date}
              onChange={(e) => {
                const value = e.target.value;
                this.setState(prevState => ({
                  formData: { ...prevState.formData, date: value },
                }), () => {
                  // Проверяем доступность для всех услуг при изменении даты
                  selectedItems.forEach((_, index) => {
                    if (selectedItems[index].rentalItemId) {
                      this.checkAvailability(index);
                    }
                  });
                });
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            {(() => {
              const hasPerHourItems = selectedItems.some(item => {
                const rentalItem = availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
                return rentalItem && (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour');
              });
              
              if (hasPerHourItems) {
                return (
                  <>
                    <TextField
                      label="Время начала *"
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => {
                        const value = e.target.value;
                        this.setState(prevState => ({
                          formData: { ...prevState.formData, startTime: value },
                        }), () => {
                          // Проверяем доступность для всех PerHour услуг при изменении времени
                          const { selectedItems, availableItems } = this.state;
                          selectedItems.forEach((item, idx) => {
                            if (item.rentalItemId) {
                              const rentalItem = availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
                              if (rentalItem && (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour')) {
                                this.checkAvailability(idx);
                              }
                            }
                          });
                        });
                      }}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      helperText="Для услуг с почасовой тарификацией"
                    />
                    <TextField
                      label="Время окончания *"
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => {
                        const value = e.target.value;
                        this.setState(prevState => ({
                          formData: { ...prevState.formData, endTime: value },
                        }), () => {
                          // Проверяем доступность для всех PerHour услуг при изменении времени
                          const { selectedItems, availableItems } = this.state;
                          selectedItems.forEach((item, idx) => {
                            if (item.rentalItemId) {
                              const rentalItem = availableItems.find(i => i.id === parseInt(item.rentalItemId, 10));
                              if (rentalItem && (rentalItem.pricingType === 1 || rentalItem.pricingType === 'PerHour')) {
                                this.checkAvailability(idx);
                              }
                            }
                          });
                        });
                      }}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      helperText="Для услуг с почасовой тарификацией"
                    />
                  </>
                );
              }
              return null;
            })()}

            <div style={{ marginTop: 24, marginBottom: 16 }}>
              <Button
                variant="outlined"
                onClick={this.handleAddItem}
                className={classes.outlinedButton}
              >
                Добавить услугу
              </Button>
            </div>

            {selectedItems.map((item, index) => {
              return (
                <Paper key={index} elevation={1} style={{ padding: 16, marginBottom: 16, backgroundColor: '#f8f9fa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      Услуга {index + 1}
                    </Typography>
                    {!this.state.editingBookingId && (
                      <Button
                        size="small"
                        onClick={() => this.handleRemoveItem(index)}
                        className={classes.removeButton}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>

                  <FormControl fullWidth margin="normal" variant="outlined">
                    <InputLabel>Услуга *</InputLabel>
                    <Select
                      value={item.rentalItemId}
                      onChange={(e) => {
                        const value = e.target.value;
                        this.handleItemChange(index, 'rentalItemId', value);
                      }}
                      label="Услуга *"
                      disabled={!!this.state.editingBookingId}
                    >
                      {availableItems.map(avItem => (
                        <MenuItem key={avItem.id} value={avItem.id}>
                          {avItem.name} ({avItem.price} ₽{(avItem.pricingType === 1 || avItem.pricingType === 'PerHour') ? '/час' : ''})
                          {avItem.quantity ? ` - ${avItem.quantity} шт.` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {this.state.availabilityInfo[index] && (
                    <div style={{ 
                      marginTop: 8, 
                      padding: 12, 
                      borderRadius: 4,
                      backgroundColor: this.state.availabilityInfo[index].available ? '#d4edda' : '#f8d7da',
                      border: `1px solid ${this.state.availabilityInfo[index].available ? '#c3e6cb' : '#f5c6cb'}`,
                    }}>
                      <Typography variant="body2" style={{ 
                        color: this.state.availabilityInfo[index].available ? '#155724' : '#721c24',
                        fontWeight: 500,
                      }}>
                        {this.state.availabilityInfo[index].available ? (
                          `✓ Доступно: ${this.state.availabilityInfo[index].availableQuantity} из ${this.state.availabilityInfo[index].totalQuantity}`
                        ) : (
                          `✗ Недостаточно! Доступно: ${this.state.availabilityInfo[index].availableQuantity} из ${this.state.availabilityInfo[index].totalQuantity}, запрошено: ${this.state.availabilityInfo[index].requestedQuantity}`
                        )}
                      </Typography>
                    </div>
                  )}

                  <TextField
                    label="Количество *"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      this.handleItemChange(index, 'quantity', value);
                    }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    inputProps={{ min: 1 }}
                    disabled={!!this.state.editingBookingId}
                    error={this.state.availabilityInfo[index] && !this.state.availabilityInfo[index].available}
                    helperText={this.state.availabilityInfo[index] && !this.state.availabilityInfo[index].available 
                      ? `Недостаточно единиц. Доступно: ${this.state.availabilityInfo[index].availableQuantity}` 
                      : ''}
                  />
                </Paper>
              );
            })}
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
            <Button onClick={this.handleCloseDialog} className={classes.outlinedButton}>
              Отмена
            </Button>
            <Button
              onClick={this.handleSave}
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : (this.state.editingBookingId ? 'Сохранить изменения' : 'Создать')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleCloseSnackbar}
          message={snackbarMessage}
        />
      </AdminPageShell>
    );
  }
}

const Styled = withStyles(adminStyles)(StandaloneBookings);
export default Styled;

